/*
 * FAHRTROUTE IN DEN KARTEN (.586), für NE3 und NE4 gleich.
 *
 * ORIGINALTEXT MARVIN (02.11.2026)
 *   „Ich würde gerne dort Adressen markieren, um dann über Google oder wie auch
 *    immer die Fahrtstrecke errechnen zu lassen ... Was ist die optimalste
 *    Route? Und wie lang ist die, wie viele Kilometer sind es und wie ist die
 *    Fahrzeit? ... vielleicht noch dann davor noch mal ein kleines Pop-Fenster
 *    dazwischen ... Route, Start, und dann beim nächsten kann ich dann
 *    auswählen, Route, Stopp und Auftragsinfo ... oder man bindet es mit dem
 *    Modal ein ... dass ich dann oben klicken kann, Karte, also Route"
 *
 * Die Halte leben in diesem Fenster (und im Browser gemerkt), damit man aus
 * der Karte in einen Auftrag und zurück gehen kann, ohne dass die Route weg ist.
 * Der erste Halt ist der Start.
 */
import { useEffect, useState } from 'react';
import { api } from '../api.ts';

export interface Halt { id: string; bereich: 'ne3' | 'ne4'; titel: string; lat: number; lon: number }

const SPEICHER = 'mds-fahrtroute';
let halte: Halt[] = (() => { try { const r = JSON.parse(localStorage.getItem(SPEICHER) ?? '[]'); return Array.isArray(r) ? r : []; } catch { return []; } })();
let ergebnis: any = null;
const hoerer = new Set<() => void>();
const melden = () => { try { localStorage.setItem(SPEICHER, JSON.stringify(halte)); } catch { /* ohne Speicher */ } ergebnis = null; hoerer.forEach((f) => f()); };

export const route = {
  halte: () => halte,
  drin: (id: string) => halte.some((h) => h.id === id),
  start: (h: Halt) => { halte = [h, ...halte.filter((x) => x.id !== h.id)]; melden(); },
  stopp: (h: Halt) => { if (!halte.some((x) => x.id === h.id)) { halte = [...halte, h]; melden(); } },
  weg: (id: string) => { halte = halte.filter((x) => x.id !== id); melden(); },
  leeren: () => { halte = []; melden(); },
};

export function useRoute(): Halt[] {
  const [, neu] = useState(0);
  useEffect(() => { const f = () => neu((n) => n + 1); hoerer.add(f); return () => { hoerer.delete(f); }; }, []);
  return halte;
}

export const haltAus = (bereich: 'ne3' | 'ne4', p: any): Halt | null =>
  p?.lat != null && p?.lon != null ? { id: String(p.id), bereich, lat: Number(p.lat), lon: Number(p.lon),
    titel: `${p.strasse ?? ''} ${p.hausnummer ?? ''}${(p.ort ?? p.ort_text) ? `, ${p.ort ?? p.ort_text}` : ''}`.trim() || String(p.kls_id ?? p.id) } : null;

const zeit = (min: number) => min >= 60 ? `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, '0')} min` : `${min} min`;
const kmText = (k: number) => `${k.toLocaleString('de-DE', { maximumFractionDigits: 1 })} km`;

/** Die Knöpfe zu einem Auftrag: Start, Stopp, heraus. */
export function RoutenKnoepfe({ h, klein = true, fertig }: { h: Halt | null; klein?: boolean; fertig?: () => void }) {
  const liste = useRoute();
  if (!h) return <span className="klein leise">ohne Koordinaten, nicht für die Route</span>;
  const k = klein ? 'knopf klein' : 'knopf';
  const ist = liste.findIndex((x) => x.id === h.id);
  return (
    <span className="reihe" style={{ gap: 6, flexWrap: 'wrap' }}>
      {ist !== 0 && <button type="button" className={k} onClick={() => { route.start(h); fertig?.(); }}>Route: Start</button>}
      {ist < 0 && liste.length > 0 && <button type="button" className={k} onClick={() => { route.stopp(h); fertig?.(); }}>Route: Stopp</button>}
      {ist >= 0 && <button type="button" className={k} onClick={() => { route.weg(h.id); fertig?.(); }}>aus der Route</button>}
    </span>
  );
}

/** Kleines Fenster am Punkt: Route oder Auftrag öffnen. */
export function PunktMenue({ x, y, breite, titel, h, oeffnen, zu }: { x: number; y: number; breite: number; titel: string; h: Halt | null; oeffnen: () => void; zu: () => void }) {
  const links = Math.max(8, Math.min(x + 12, breite - 268));
  return (
    <div className="routen-menue" style={{ left: links, top: Math.max(8, y - 12) }} onMouseDown={(e) => e.stopPropagation()} onTouchStart={(e) => e.stopPropagation()}>
      <div className="reihe" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 6 }}>
        <b className="klein">{titel}</b>
        <button type="button" className="knopf klein" aria-label="schließen" onClick={zu}>×</button>
      </div>
      <div className="routen-menue-knoepfe">
        <button type="button" className="knopf klein haupt" onClick={oeffnen}>Auftrag öffnen</button>
        <RoutenKnoepfe h={h} fertig={zu} />
      </div>
    </div>
  );
}

/** Die Linie der Route auf der Karte, mit Nummern. `lage` rechnet Koordinaten in Bildpunkte. */
export function RoutenEbene({ lage }: { lage: (o: { lat: number; lon: number }) => { x: number; y: number } }) {
  const liste = useRoute();
  if (liste.length < 1) return null;
  const folge: number[] = ergebnis?.reihenfolge?.length === liste.length ? ergebnis.reihenfolge : liste.map((_, i) => i);
  const pkt = folge.map((i) => lage(liste[i]!));
  if (ergebnis?.rund && pkt.length > 1) pkt.push(pkt[0]!);
  return (
    <g className="routen-ebene" style={{ pointerEvents: 'none' }}>
      {/* .587: mit Ergebnis der Straßenverlauf (Marvin: „sieht für mich aus eher wie Luftlinie anstatt Fahrlinie"),
          vorher gestrichelt die Luftlinie zwischen den Halten. */}
      {Array.isArray(ergebnis?.linie) && ergebnis.linie.length > 1
        ? <polyline points={(ergebnis.linie as [number, number][]).filter((_, i, l) => l.length < 1500 || i % Math.ceil(l.length / 1500) === 0 || i === l.length - 1)
                              .map(([la, lo]) => { const q = lage({ lat: la, lon: lo }); return `${q.x.toFixed(1)},${q.y.toFixed(1)}`; }).join(' ')}
                    fill="none" stroke="#e20074" strokeWidth={4} strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
        : pkt.length > 1 && <polyline points={pkt.map((q) => `${q.x},${q.y}`).join(' ')} fill="none" stroke="#e20074" strokeWidth={3} strokeDasharray="6 5" strokeLinejoin="round" opacity={0.85} />}
      {folge.map((i, n) => { const q = lage(liste[i]!); return (
        <g key={liste[i]!.id}>
          <circle cx={q.x} cy={q.y - 18} r={9} fill={n === 0 ? '#1f2a44' : '#e20074'} stroke="#fff" strokeWidth={2} />
          <text x={q.x} y={q.y - 14} fontSize={10} fontWeight={700} fill="#fff" textAnchor="middle">{n === 0 ? 'S' : n}</text>
        </g>
      ); })}
    </g>
  );
}

/** Die Leiste über der Karte: Halte, Ende, berechnen, Ergebnis. */
export function RoutenLeiste({ oeffnen }: { oeffnen?: (h: Halt) => void }) {
  const liste = useRoute();
  const [rund, setRund] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [, neu] = useState(0);
  /* Die Leiste ändert ihre Höhe: die Karte misst sich dann neu (kein Rollbalken). */
  useEffect(() => { const r = requestAnimationFrame(() => window.dispatchEvent(new Event('resize'))); return () => cancelAnimationFrame(r); });
  useEffect(() => () => { setTimeout(() => window.dispatchEvent(new Event('resize')), 0); }, []);
  if (!liste.length) return null;
  const e = ergebnis;
  async function rechnen() {
    setLaeuft(true); setFehler(null);
    try { ergebnis = await api.fahrtroute(liste.map((h) => ({ lat: h.lat, lon: h.lon })), rund); hoerer.forEach((f) => f()); neu((n) => n + 1); }
    catch (f: any) { setFehler(String(f?.message ?? f)); } finally { setLaeuft(false); }
  }
  const reihe: Halt[] = e?.reihenfolge?.length === liste.length ? e.reihenfolge.map((i: number) => liste[i]!) : liste;
  return (
    <div className="routen-leiste">
      <div className="reihe" style={{ gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Route</b>
        {reihe.map((h, n) => (
          <span key={h.id} className="routen-halt" title={h.bereich.toUpperCase()}>
            <span className={'routen-nr' + (n === 0 ? ' start' : '')}>{n === 0 ? 'S' : n}</span>
            {oeffnen ? <button type="button" className="alslink" onClick={() => oeffnen(h)}>{h.titel}</button> : h.titel}
            <button type="button" className="routen-weg" aria-label={`${h.titel} aus der Route`} onClick={() => route.weg(h.id)}>×</button>
          </span>
        ))}
        {liste.length < 2 && <span className="klein leise">Start gesetzt. Jetzt Stopps wählen: Punkt anklicken, „Route: Stopp".</span>}
        <span className="wachsen" />
        <label className="klein" style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
          <input type="checkbox" checked={rund} onChange={(x) => { setRund(x.target.checked); ergebnis = null; neu((n) => n + 1); hoerer.forEach((f) => f()); }} /> zurück zum Start
        </label>
        <button type="button" className="knopf klein haupt" disabled={laeuft || liste.length < 2} onClick={() => void rechnen()}>{laeuft ? 'rechnet …' : 'Route berechnen'}</button>
        <button type="button" className="knopf klein" onClick={() => route.leeren()}>leeren</button>
      </div>
      {fehler && <div className="hinweis fehler" style={{ marginTop: 6 }}>{fehler}</div>}
      {e && (
        <div className="routen-ergebnis">
          <span className="routen-zahl">{kmText(e.km)}</span>
          <span className="routen-zahl">{zeit(e.minuten)}</span>
          <span className="klein leise">{e.quelle === 'google' ? 'beste Reihenfolge und Straßenverlauf von Google' : e.quelle === 'osrm' ? 'beste Reihenfolge und Straßenverlauf von OpenStreetMap' : 'geschätzt nach Luftlinie'}{e.rund ? ', zurück zum Start' : ''}</span>
          {!!e.abschnitte?.length && <span className="klein leise">{e.abschnitte.map((a: any, i: number) => `${i === 0 ? 'S' : i}→${e.rund && i === e.abschnitte.length - 1 ? 'S' : i + 1}: ${kmText(a.km)}, ${zeit(a.minuten)}`).join(', ')}</span>}
          <a className="knopf klein" href={e.link} target="_blank" rel="noreferrer">In Google Maps öffnen</a>
          {e.hinweis && <div className="klein leise" style={{ width: '100%' }}>{e.hinweis}</div>}
        </div>
      )}
    </div>
  );
}
